/**
 * Pipeline Intelligence Context
 *
 * MODULE OWNERSHIP: Source Config + Pipeline Execution (see moduleOwnership.ts)
 * 
 * Central state provider for source profiles, response flows,
 * AI configuration, active sequences, and performance data.
 * 
 * This is the EXECUTION ENGINE for all lead response orchestration.
 * Source Profile defines baseline; this context can override per-flow.
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import type {
  SourceProfile, ResponseFlow, AISourceConfig, AIComplianceRule,
  AIConversationDraft, ActiveSequence, ResponseTemplate, PerformanceInsight,
  SourcePerformanceMetrics, PipelineAuditEntry,
} from "./types";

interface PipelineIntelligenceContextType {
  // Source profiles
  sourceProfiles: SourceProfile[];
  updateSourceProfile: (id: string, updates: Partial<SourceProfile>) => void;

  // Response flows
  flows: ResponseFlow[];
  addFlow: (flow: ResponseFlow) => void;
  updateFlow: (id: string, updates: Partial<ResponseFlow>) => void;
  toggleFlow: (id: string) => void;
  deleteFlow: (id: string) => void;

  // Templates
  templates: ResponseTemplate[];
  addTemplate: (tpl: ResponseTemplate) => void;
  updateTemplate: (id: string, updates: Partial<ResponseTemplate>) => void;

  // AI config
  aiConfigs: AISourceConfig[];
  updateAIConfig: (sourceId: string, updates: Partial<AISourceConfig>) => void;
  complianceRules: AIComplianceRule[];
  updateComplianceRule: (id: string, updates: Partial<AIComplianceRule>) => void;
  addComplianceRule: (rule: AIComplianceRule) => void;

  // AI drafts
  aiDrafts: AIConversationDraft[];
  approveDraft: (id: string, reviewer: string) => void;
  rejectDraft: (id: string, reviewer: string) => void;

  // Sequences
  activeSequences: ActiveSequence[];
  pauseSequence: (id: string) => void;
  stopSequence: (id: string, reason: string) => void;

  // Performance
  performanceMetrics: SourcePerformanceMetrics[];
  insights: PerformanceInsight[];

  // Audit
  auditLog: PipelineAuditEntry[];
  addAuditEntry: (entry: Omit<PipelineAuditEntry, "id" | "changedAt">) => void;
}

const PipelineIntelligenceContext = createContext<PipelineIntelligenceContextType | null>(null);

export function PipelineIntelligenceProvider({ children }: { children: React.ReactNode }) {
  const [sourceProfiles, setSourceProfiles] = useState<SourceProfile[]>([]);
  const [flows, setFlows] = useState<ResponseFlow[]>([]);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [aiConfigs, setAIConfigs] = useState<AISourceConfig[]>([]);
  const [complianceRules, setComplianceRules] = useState<AIComplianceRule[]>([]);
  const [aiDrafts, setAIDrafts] = useState<AIConversationDraft[]>([]);
  const [activeSequences, setActiveSequences] = useState<ActiveSequence[]>([]);
  const [performanceMetrics] = useState<SourcePerformanceMetrics[]>([]);
  const [insights] = useState<PerformanceInsight[]>([]);
  const [auditLog, setAuditLog] = useState<PipelineAuditEntry[]>([]);

  const addAuditEntry = useCallback((entry: Omit<PipelineAuditEntry, "id" | "changedAt">) => {
    setAuditLog(prev => [{ ...entry, id: `aud-${Date.now()}`, changedAt: new Date().toISOString() }, ...prev]);
  }, []);

  const updateSourceProfile = useCallback((id: string, updates: Partial<SourceProfile>) => {
    setSourceProfiles(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    addAuditEntry({ action: "source_profile_updated", entityId: id, entityName: sourceProfiles.find(s => s.id === id)?.sourceName || "", changedBy: "Operator", details: `Updated: ${Object.keys(updates).join(", ")}` });
  }, [sourceProfiles, addAuditEntry]);

  const addFlow = useCallback((flow: ResponseFlow) => {
    setFlows(prev => [...prev, flow]);
    addAuditEntry({ action: "flow_created", entityId: flow.id, entityName: flow.name, changedBy: "Operator", details: `Created flow with ${flow.steps.length} steps` });
  }, [addAuditEntry]);

  const updateFlow = useCallback((id: string, updates: Partial<ResponseFlow>) => {
    setFlows(prev => prev.map(f => f.id === id ? { ...f, ...updates, version: f.version + 1, updatedAt: new Date().toISOString() } : f));
  }, []);

  const toggleFlow = useCallback((id: string) => {
    setFlows(prev => prev.map(f => f.id === id ? { ...f, enabled: !f.enabled } : f));
    const flow = flows.find(f => f.id === id);
    if (flow) addAuditEntry({ action: "flow_toggled", entityId: id, entityName: flow.name, changedBy: "Operator", details: flow.enabled ? "Disabled" : "Enabled" });
  }, [flows, addAuditEntry]);

  const deleteFlow = useCallback((id: string) => {
    setFlows(prev => prev.filter(f => f.id !== id));
  }, []);

  const addTemplate = useCallback((tpl: ResponseTemplate) => {
    setTemplates(prev => [...prev, tpl]);
  }, []);

  const updateTemplate = useCallback((id: string, updates: Partial<ResponseTemplate>) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  }, []);

  const updateAIConfig = useCallback((sourceId: string, updates: Partial<AISourceConfig>) => {
    setAIConfigs(prev => prev.map(c => c.sourceId === sourceId ? { ...c, ...updates } : c));
    addAuditEntry({ action: "ai_config_changed", entityId: sourceId, entityName: aiConfigs.find(c => c.sourceId === sourceId)?.sourceName || "", changedBy: "Operator", details: `Changed: ${Object.keys(updates).join(", ")}` });
  }, [aiConfigs, addAuditEntry]);

  const updateComplianceRule = useCallback((id: string, updates: Partial<AIComplianceRule>) => {
    setComplianceRules(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  const addComplianceRule = useCallback((rule: AIComplianceRule) => {
    setComplianceRules(prev => [...prev, rule]);
  }, []);

  const approveDraft = useCallback((id: string, reviewer: string) => {
    setAIDrafts(prev => prev.map(d => d.id === id ? { ...d, status: "approved", reviewedBy: reviewer, reviewedAt: new Date().toISOString() } : d));
    addAuditEntry({ action: "ai_message_sent", entityId: id, entityName: aiDrafts.find(d => d.id === id)?.leadName || "", changedBy: reviewer, details: "Approved AI-generated message" });
  }, [aiDrafts, addAuditEntry]);

  const rejectDraft = useCallback((id: string, reviewer: string) => {
    setAIDrafts(prev => prev.map(d => d.id === id ? { ...d, status: "rejected", reviewedBy: reviewer, reviewedAt: new Date().toISOString() } : d));
    addAuditEntry({ action: "ai_message_rejected", entityId: id, entityName: aiDrafts.find(d => d.id === id)?.leadName || "", changedBy: reviewer, details: "Rejected AI-generated message" });
  }, [aiDrafts, addAuditEntry]);

  const pauseSequence = useCallback((id: string) => {
    setActiveSequences(prev => prev.map(s => s.id === id ? { ...s, status: s.status === "paused" ? "active" : "paused" } : s));
  }, []);

  const stopSequence = useCallback((id: string, reason: string) => {
    setActiveSequences(prev => prev.map(s => s.id === id ? { ...s, status: "stopped", stoppedReason: reason } : s));
  }, []);

  return (
    <PipelineIntelligenceContext.Provider value={{
      sourceProfiles, updateSourceProfile,
      flows, addFlow, updateFlow, toggleFlow, deleteFlow,
      templates, addTemplate, updateTemplate,
      aiConfigs, updateAIConfig, complianceRules, updateComplianceRule, addComplianceRule,
      aiDrafts, approveDraft, rejectDraft,
      activeSequences, pauseSequence, stopSequence,
      performanceMetrics, insights,
      auditLog, addAuditEntry,
    }}>
      {children}
    </PipelineIntelligenceContext.Provider>
  );
}

export function usePipelineIntelligence() {
  const ctx = useContext(PipelineIntelligenceContext);
  if (!ctx) throw new Error("usePipelineIntelligence must be inside PipelineIntelligenceProvider");
  return ctx;
}